import { createFileRoute, useNavigate } from "@tanstack/react-router";

import ActionButton from "@/components/Button/ActionButton";
import { VStack } from "@/components/Layout";
import { ViewportLoader } from "@/components/Loading";
import { ProductLogoMediumOrganizationPlanning } from "@/components/Logo/Product";
import type { TabWithIndicatorProps } from "@/components/Navigation/HeaderTabs";
import { ProductHeader } from "@/components/ProductHeader";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { defaultNS, useTranslation } from "@/lib/i18n";

import { EmptyState } from "./-components/EmptyState";
import { PlanDataTable } from "./-components/PlanDataTable";
import { useDepartmentRestructuringsQuery } from "./-query";

export const Route = createFileRoute("/_staff/organization/plan/")({
  component: RouteComponent,
});

function RouteComponent() {
  const { data, isPending } = useDepartmentRestructuringsQuery();
  const navigate = useNavigate();

  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.organization.plan.emptyState",
  });

  const handleCreate = () => {
    navigate({ to: "/organization/plan/create" });
  };

  return (
    <VStack>
      <Header />
      <VStack className="h-full gap-3 px-12 py-5">
        {isPending ? (
          <ViewportLoader />
        ) : data && data.length === 0 ? (
          <EmptyState onCreate={handleCreate} className="h-full" />
        ) : (
          <VStack className="h-full gap-3">
            <ActionButton
              icon="PlusIcon"
              iconColor="none"
              onClick={handleCreate}
              className="w-fit self-end"
            >
              {t("createOrganizationPlan")}
            </ActionButton>
            <PlanDataTable data={data ?? []} isLoading={isPending} />
          </VStack>
        )}
      </VStack>
    </VStack>
  );
}

function Header() {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.organization.plan",
  });
  useDocumentTitle(t("title"));

  const tabs = [
    {
      to: "/organization/plan",
      children: t("tabs.plan"),
      activeOptions: { exact: true },
    },
  ] satisfies TabWithIndicatorProps[];

  return (
    <ProductHeader
      Logo={ProductLogoMediumOrganizationPlanning}
      name={t("title")}
      tabs={tabs}
      type="resource"
    />
  );
}
